/**
 * apiClient.js — Centralized, auth-aware HTTP client.
 *
 * All API calls in the app MUST go through this module so that:
 *  1. The JWT access token is automatically injected into every request.
 *  2. 401 responses trigger an immediate logout redirect.
 *  3. Error handling is consistent across the codebase.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8002';
const GET_CACHE_TTL_MS = 30_000;
const getCache = new Map();
const pendingGetRequests = new Map();

const isCacheableGet = (path) => {
  const normalized = String(path || '');
  return (
    normalized.startsWith('/api/dimension-values/') ||
    normalized.startsWith('/api/customer-orders/options') ||
    normalized.startsWith('/api/data-summary') ||
    normalized.startsWith('/api/config/regions')
  );
};

export const clearApiCache = () => {
  getCache.clear();
  pendingGetRequests.clear();
};

/** Retrieve the stored JWT token (or null if not logged in). */
const getToken = () => localStorage.getItem('access_token');

/**
 * Core fetch wrapper.
 * @param {string} path - API path, e.g. '/api/data-summary'
 * @param {RequestInit} options - Standard fetch options (method, body, etc.)
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiFetch = async (path, options = {}) => {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });
  } catch (networkError) {
    throw new Error(`Network error — the server may be temporarily busy. Please try again in a few seconds.`);
  }

  // If we sent a token and it was rejected, retry once without auth.
  // The backend allows anonymous access; stale local tokens should not break the app.
  if (response.status === 401 && token) {
    const retryHeaders = { ...headers };
    delete retryHeaders.Authorization;
    let retryResponse;
    try {
      retryResponse = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: retryHeaders,
      });
    } catch (retryNetworkError) {
      throw new Error(`Network error — the server may be temporarily busy. Please try again in a few seconds.`);
    }
    if (retryResponse.ok) {
      const retryContentType = retryResponse.headers.get('content-type') || '';
      if (retryContentType.includes('application/json')) {
        return retryResponse.json();
      }
      return retryResponse.text();
    }
  }

  // If the server still rejects the request, clear session and redirect to login.
  if (response.status === 401) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const errorJson = await response.json();
        if (errorJson && errorJson.detail) {
          errorMessage = errorJson.detail;
        }
      }
    } catch (parseError) {
      // Ignore body parsing errors
    }
    throw new Error(errorMessage);
  }

  // Some endpoints return plain text (e.g. CSV downloads).
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return response.text();
};

// ─── Convenience Methods ────────────────────────────────────────────────────

export const apiGet = (path) => {
  if (!isCacheableGet(path)) {
    return apiFetch(path, { method: 'GET' });
  }

  const cached = getCache.get(path);
  if (cached && Date.now() - cached.ts < GET_CACHE_TTL_MS) {
    return Promise.resolve(cached.data);
  }

  const pending = pendingGetRequests.get(path);
  if (pending) {
    return pending;
  }

  const request = apiFetch(path, { method: 'GET' })
    .then((data) => {
      getCache.set(path, { ts: Date.now(), data });
      return data;
    })
    .finally(() => {
      pendingGetRequests.delete(path);
    });
  pendingGetRequests.set(path, request);
  return request;
};

export const apiPost = (path, body) =>
  apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
  }).then((result) => {
    if (String(path || '').includes('/adhoc')) {
      clearApiCache();
    }
    return result;
  });

export const apiPatch = (path, body) =>
  apiFetch(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }).then((result) => {
    clearApiCache();
    return result;
  });

// ─── Dimension / Dropdown Helpers ───────────────────────────────────────────

/** Fetch all dropdown options (brands, SKUs, channels, locations) in one shot. */
export const fetchDimensionOptions = async () => {
  const [brandsRes, skusRes, channelsRes, locationsRes, regionsRes] = await Promise.all([
    apiFetch('/api/dimension-values/brand?limit=200').catch(() => ({ values: [] })),
    apiFetch('/api/dimension-values/sku_id?limit=500&include_metadata=true').catch(() => ({ values: [], metadata: {} })),
    apiFetch('/api/dimension-values/channel?limit=100').catch(() => ({ values: [] })),
    apiFetch('/api/dimension-values/location?limit=100&include_metadata=true').catch(() => ({ values: [], metadata: {} })),
    apiFetch('/api/dimension-values/region?limit=100').catch(() => ({ values: [] })),
  ]);

  const locationMetadata = locationsRes.metadata || {};
  const skuMetadata = skusRes.metadata || {};

  return {
    brands: brandsRes.values || [],
    skus: skusRes.values || [],
    channels: channelsRes.values || [],
    locations: locationsRes.values || [],
    regions: regionsRes.values || [],
    locationRegionMap: locationMetadata.region_map || {},
    skuBrandMap: skuMetadata.sku_brand_map || {},
  };
};

/** Fetch SKUs filtered by brand (pass empty string for all). */
export const fetchSkusByBrand = async (brand) => {
  const url = brand
    ? `/api/customer-orders/options?column=skus&brand=${encodeURIComponent(brand)}`
    : '/api/customer-orders/options?column=skus';
  const res = await apiFetch(url).catch(() => ({ skus: [] }));
  return res.skus || [];
};

/** Fetch live region profiles used by the regional configuration UI. */
export const fetchRegionConfig = async () => {
  const res = await apiGet('/api/config/regions').catch(() => ({ regions: [], profiles: {} }));
  return {
    regions: res.regions || [],
    profiles: res.profiles || {},
  };
};

export default apiFetch;
